import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 

export default function BehaviorPortal({ currentUser }) {
  const [availableSprints, setAvailableSprints] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [portalConfig, setPortalConfig] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Form States
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [scores, setScores] = useState({});
  const [teacherComment, setTeacherComment] = useState('');

  // 1. Fetch Teacher's Building Rules & Calendar on Load
  useEffect(() => {
    const initializePortal = async () => {
      try {
        let defaultSprint = '';
        let sprintsList = [];
        const calRef = doc(db, `districts/${currentUser.tenantId}/settings/calendar`);
        const calSnap = await getDoc(calRef);
        if (calSnap.exists()) {
          sprintsList = calSnap.data().sprints || [];
          defaultSprint = calSnap.data().activeSprint || '';
          setAvailableSprints(sprintsList);
        }

        const schoolId = currentUser?.schoolId || 'District Office';
        const configRef = doc(db, `districts/${currentUser.tenantId}/settings`, `rubric_${schoolId}`);
        const configSnap = await getDoc(configRef);
        
        let config = { 
          frequency: 'Sprint', 
          customScale: [{ value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }], 
          metrics: ['On Task', 'Shows Respect'] 
        };
        
        if (configSnap.exists()) {
          const data = configSnap.data();
          if (data.customScale) config = data; // Ensure it has the new array
        }
        setPortalConfig(config);

        if (config.frequency === 'Daily' || config.frequency === 'Weekly') {
          setSelectedPeriod(new Date().toISOString().split('T')[0]);
        } else {
          setSelectedPeriod(defaultSprint);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load portal configuration:", error);
        setIsLoading(false);
      }
    };

    if (currentUser?.tenantId) initializePortal();
  }, [currentUser]);

  // 2. Live Roster Fetch
  useEffect(() => {
    const fetchTeacherRoster = async () => {
      if (!selectedPeriod) return; 
      setIsLoading(true); 
      try {
        const studentsRef = collection(db, `districts/${currentUser.tenantId}/students`);
        const rosterQuery = query(studentsRef, where("assignedGenEd", "array-contains", auth.currentUser.uid));
        const rosterSnapshot = await getDocs(rosterQuery);

        const liveRoster = [];

        for (const docSnapshot of rosterSnapshot.docs) {
          const studentData = docSnapshot.data();
          const studentId = docSnapshot.id;
          const rawName = studentData.name || studentData.studentName || studentData.fullName || 'Student';

          const logsRef = collection(db, `districts/${currentUser.tenantId}/students/${studentId}/behaviorLogs`);
          const completedQuery = query(logsRef, where("teacherId", "==", auth.currentUser.uid), where("sprint", "==", selectedPeriod));
          const logsSnapshot = await getDocs(completedQuery);

          liveRoster.push({
            id: studentId,
            name: rawName,
            status: logsSnapshot.empty ? 'pending' : 'completed' 
          });
        }

        setAssignedStudents(liveRoster);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch roster:", error);
        setIsLoading(false);
      }
    };

    if (auth.currentUser?.uid && portalConfig) fetchTeacherRoster();
  }, [currentUser?.tenantId, selectedPeriod, portalConfig]); 

  // 3. Submit
  const submitBehaviorData = async () => {
    try {
      const logsRef = collection(db, `districts/${currentUser.tenantId}/students/${selectedStudent.id}/behaviorLogs`);
      
      await addDoc(logsRef, {
        teacherId: auth.currentUser.uid, 
        sprint: selectedPeriod,
        // No longer saving the string, saving the config so we have a paper trail
        scores: scores,
        comment: teacherComment.trim(), 
        timestamp: serverTimestamp()
      });

      setAssignedStudents(prevRoster => prevRoster.map(s => s.id === selectedStudent.id ? { ...s, status: 'completed' } : s));
      setSelectedStudent(null);
      setScores({});
      setTeacherComment('');
    } catch (error) {
      console.error("Submission failed:", error);
      alert("Could not submit data.");
    }
  };

  const completedCount = assignedStudents.filter(s => s.status === 'completed').length;
  const isInboxZero = assignedStudents.length > 0 && completedCount === assignedStudents.length;
  const isFormComplete = portalConfig && portalConfig.metrics.every(m => scores[m] !== undefined);

  if (isLoading && !portalConfig) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f7f6' }}><p style={{ fontWeight: 'bold', color: '#666' }}>Loading Portal...</p></div>;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      <div style={{ marginBottom: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '5px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#003366', margin: 0 }}>Behavior Progress</h1>
          <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase' }}>
            {portalConfig?.frequency} Tracking
          </span>
        </div>
        
        {portalConfig?.frequency === 'Daily' || portalConfig?.frequency === 'Weekly' ? (
          <input 
            type="date" 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)} 
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #003366', fontWeight: 'bold', marginTop: '5px', backgroundColor: '#fff', fontSize: '15px', color: '#333' }}
          />
        ) : (
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)} 
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #003366', fontWeight: 'bold', marginTop: '5px', backgroundColor: '#fff', fontSize: '15px', color: '#333' }}
          >
            {availableSprints.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <span style={{ fontWeight: 'bold' }}>Submission Status</span>
          <span style={{ fontWeight: 'bold', color: isInboxZero ? '#28a745' : '#003366' }}>{completedCount} / {assignedStudents.length}</span>
        </div>
      </div>

      {!selectedStudent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {assignedStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666', backgroundColor: '#fff', borderRadius: '8px', border: '2px dashed #ccc' }}>
              No students assigned to you.
            </div>
          ) : (
            assignedStudents.map((student) => (
              <button
                key={student.id}
                onClick={() => setSelectedStudent(student)}
                disabled={student.status === 'completed'}
                style={{
                  width: '100%', display: 'flex', justifyContent: 'space-between', padding: '20px', borderRadius: '12px', border: student.status === 'completed' ? '1px solid #eee' : '1px solid #003366',
                  backgroundColor: student.status === 'completed' ? '#f9f9f9' : '#fff', cursor: student.status === 'completed' ? 'not-allowed' : 'pointer', opacity: student.status === 'completed' ? 0.7 : 1,
                  boxShadow: student.status === 'completed' ? 'none' : '0 4px 6px rgba(0,0,0,0.1)'
                }}
              >
                <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#333' }}>{student.name}</span>
                <span style={{ backgroundColor: student.status === 'completed' ? '#e6ffe6' : '#ffe6e6', color: student.status === 'completed' ? '#006600' : '#ba0c2f', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
                  {student.status === 'completed' ? '✓ Done' : 'Due'}
                </span>
              </button>
            ))
          )}
        </div>
      ) : (
        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '12px', border: '1px solid #ddd', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', borderBottom: '2px solid #eee', paddingBottom: '15px' }}>
            <div>
              <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: '0 0 5px 0', color: '#003366' }}>Logging {selectedStudent.name}</h2>
            </div>
            <button 
              onClick={() => { setSelectedStudent(null); setScores({}); setTeacherComment(''); }} 
              style={{ background: 'none', border: 'none', fontSize: '28px', color: '#999', cursor: 'pointer', lineHeight: '1' }}
            >
              &times;
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
            {portalConfig?.metrics.map(metric => (
              <div key={metric}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px', color: '#333', fontSize: '16px' }}>{metric}</label>
                
                {/* THE DYNAMIC MAP: This handles emojis, numbers, letters, anything! */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                  {portalConfig.customScale.map((btn) => (
                    <button
                      key={`${metric}-${btn.value}`}
                      onClick={() => setScores({...scores, [metric]: btn.value})}
                      style={{
                        flex: 1, padding: '15px 5px', fontSize: '16px', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                        backgroundColor: scores[metric] === btn.value ? '#003366' : '#f0f0f0', color: scores[metric] === btn.value ? '#fff' : '#555',
                        transform: scores[metric] === btn.value ? 'scale(1.05)' : 'scale(1)',
                        lineHeight: '1.2'
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ marginTop: '10px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#333', fontSize: '16px' }}>Teacher Notes <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>(Optional)</span></label>
              <textarea 
                value={teacherComment} 
                onChange={(e) => setTeacherComment(e.target.value)} 
                placeholder="Add context here..."
                style={{ width: '100%', padding: '15px', borderRadius: '8px', border: '1px solid #ccc', boxSizing: 'border-box', minHeight: '100px', resize: 'vertical', fontFamily: 'inherit', fontSize: '14px' }}
              />
            </div>
          </div>

          <button 
            disabled={!isFormComplete}
            style={{ width: '100%', marginTop: '30px', padding: '18px', borderRadius: '8px', border: 'none', fontSize: '18px', fontWeight: 'bold', transition: 'all 0.2s', backgroundColor: isFormComplete ? '#28a745' : '#e0e0e0', color: isFormComplete ? '#fff' : '#999', cursor: isFormComplete ? 'pointer' : 'not-allowed', boxShadow: isFormComplete ? '0 4px 12px rgba(40, 167, 69, 0.3)' : 'none' }}
            onClick={submitBehaviorData}
          >
            Submit Behavior Data
          </button>
        </div>
      )}
    </div>
  );
}